import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import LandingPage from './pages/auth/LandingPage'
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import OnboardingPage from './pages/auth/OnboardingPage'
import AuthActionPage from './pages/auth/AuthActionPage'
import { onboardingEnabled } from './lib/businessAccessClient'
import AccountAccessBoundary from './components/AccountAccessBoundary'
import RedirectToDefaultPlace from './components/RedirectToDefaultPlace'
import { OrderAlertSetupModal } from './components/OrderAlertSetupModal'
import { checkOrderAlertReadiness } from './lib/nativeOrderAlerts'
import { shouldShowNativeSetup } from './lib/nativeSetupGate'
import { getRootRouteTarget } from './lib/authRoutingPolicy'
import { HungSuspenseFallback, PosAppErrorBoundary } from './components/PosAppErrorBoundary'

const POSApp = lazy(() => import('./App'))

function AuthBootStatus() {
  const auth = useAuth()
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      {auth?.authRestoreSlow ? 'Still restoring your session…' : 'Loading…'}
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactElement }) {
  const auth = useAuth()
  const location = useLocation()

  if (auth?.loading || !auth?.authReady) {
    return <AuthBootStatus />
  }

  if (!auth?.user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  const runtime = <ProtectedRouteRuntime>{children}</ProtectedRouteRuntime>
  return onboardingEnabled ? <AccountAccessBoundary>{runtime}</AccountAccessBoundary> : runtime
}

function ProtectedRouteRuntime({ children }: { children: React.ReactElement }) {
  const [setupOpen, setSetupOpen] = useState(false)

  const evaluateSetup = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) return
    if (!(await shouldShowNativeSetup())) return
    const readiness = await checkOrderAlertReadiness()
    setSetupOpen(!readiness.ready)
  }, [])

  useEffect(() => {
    void evaluateSetup()
  }, [evaluateSetup])

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    let cancelled = false
    let removeListener: (() => void) | undefined
    void import('@capacitor/app').then(async ({ App }) => {
      const handle = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive && !cancelled) void evaluateSetup()
      })
      removeListener = () => void handle.remove()
    })
    return () => {
      cancelled = true
      removeListener?.()
    }
  }, [evaluateSetup])

  return (
    <>
      {children}
      <OrderAlertSetupModal open={setupOpen} onClose={() => setSetupOpen(false)} />
    </>
  )
}

function RootRoute() {
  const auth = useAuth()
  const target = getRootRouteTarget(Boolean(auth?.authReady), Boolean(auth?.user))
  if (target === 'loading') {
    return <AuthBootStatus />
  }
  if (target === 'app') {
    return <Navigate to="/app" replace />
  }
  return <LandingPage />
}

function GuestOnlyRoute({ children }: { children: React.ReactElement }) {
  const auth = useAuth()

  if (auth?.loading || !auth?.authReady) {
    return <AuthBootStatus />
  }

  if (auth?.user) {
    return <Navigate to="/app" replace />
  }

  return children
}

export default function AuthShell() {
  const auth = useAuth()
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/onboarding/*" element={<OnboardingPage key={auth?.user?.uid ?? 'signed-out'} />} />
      <Route path="/auth/action" element={<AuthActionPage />} />
      <Route
        path="/login"
        element={
          <GuestOnlyRoute>
            <LoginPage />
          </GuestOnlyRoute>
        }
      />
      <Route
        path="/register"
        element={
          <GuestOnlyRoute>
            <RegisterPage />
          </GuestOnlyRoute>
        }
      />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <RedirectToDefaultPlace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/:placeId/*"
        element={
          <ProtectedRoute>
            <PosAppErrorBoundary>
              <Suspense fallback={<HungSuspenseFallback />}>
                <POSApp />
              </Suspense>
            </PosAppErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
